import { useState } from 'react'
import { X, AlertCircle, Loader } from 'lucide-react'
import { connectJudgeMe } from '../../services/judgemeApi'

interface Props {
  jwt: string
  locationId: string
  onSuccess: () => void
  onClose: () => void
}

export default function JudgeMeConnectModal({ jwt, locationId, onSuccess, onClose }: Props) {
  const [apiToken, setApiToken] = useState('')
  const [shopDomain, setShopDomain] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const domain = shopDomain.trim()
    const token = apiToken.trim()

    if (!token) { setError('API token is required.'); return }
    if (!domain) { setError('Shop domain is required.'); return }

    setIsSubmitting(true)
    try {
      await connectJudgeMe(jwt, locationId, token, domain)
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-rd-surface border border-white/[0.07] rounded-2xl p-6 w-full max-w-md mx-4 space-y-5 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="font-sora font-semibold text-lg text-rd-text">Connect Judge.me</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-rd-muted hover:text-rd-text transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* API Token */}
          <div className="space-y-1.5">
            <label className="block text-sm text-rd-muted" htmlFor="jm-token">
              API Token
            </label>
            <input
              id="jm-token"
              type="password"
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
              placeholder="••••••••••••••••"
              autoComplete="off"
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-rd-text placeholder-rd-dim focus:outline-none focus:border-rd-accent/50 transition-colors"
            />
            <p className="text-xs text-rd-dim">
              Find it in your Judge.me dashboard → Settings → API → Access Token.
            </p>
          </div>

          {/* Shop Domain */}
          <div className="space-y-1.5">
            <label className="block text-sm text-rd-muted" htmlFor="jm-domain">
              Shop Domain
            </label>
            <input
              id="jm-domain"
              type="text"
              value={shopDomain}
              onChange={(e) => setShopDomain(e.target.value)}
              placeholder="my-store.myshopify.com"
              autoComplete="off"
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-rd-text placeholder-rd-dim focus:outline-none focus:border-rd-accent/50 transition-colors"
            />
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-danger-500 flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-rd-muted hover:text-rd-text border border-white/10 hover:border-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #4ade80, #22d3ee)', color: '#0a0a0f' }}
            >
              {isSubmitting && <Loader className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {isSubmitting ? 'Connecting…' : 'Connect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
